import React, { useState } from "react";
import { Link } from "react-router-dom";

const PERIODS = [
  ["lifetime", "Lifetime"], ["last_24_months", "Last 2 years"], ["last_12_months", "Last 12 months"],
  ["this_fy", "This financial year"], ["last_30_days", "Last 30 days"],
];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const day = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

function Stat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[11px] uppercase font-semibold text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function CustomerBilling({ data, isAdmin }) {
  const [period, setPeriod] = useState("lifetime");
  if (!data) return null;
  const P = data.periods[period];
  const maxBilled = Math.max(1, ...data.monthly.map((m) => m.billed));
  const badge = { PAID: "bg-emerald-100 text-emerald-700", UNPAID: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-4" data-testid="customer-billing">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wide">Billing &amp; Activity</h2>
        <div className="flex flex-wrap gap-1">
          {PERIODS.map(([k, label]) => (
            <button key={k} onClick={() => setPeriod(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${period === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Shipments" value={P.shipments} />
        <Stat label="Total billed" value={inr(P.billed)} />
        <Stat label="Invoiced" value={`${inr(P.invoiced)} (${P.invoices})`} />
        <Stat label="Payment due" value={inr(P.due)} tone={P.due > 0 ? "text-red-600" : "text-emerald-700"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Paid invoices" value={inr(P.paid)} tone="text-emerald-700" />
        <Stat label="Avg per shipment" value={inr(data.avg_order_value)} />
        <Stat label="First booking" value={day(data.first_booking)} />
        <Stat label="Last booking" value={day(data.last_booking)} />
      </div>
      <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
        <span>Segment: <b>{data.segment}</b></span>
        <span>Wallet: <b>{inr(data.wallet.balance)}</b>{data.wallet.credit_limit > 0 && <> · credit limit {inr(data.wallet.credit_limit)}</>}</span>
        <span>Cancelled: <b>{data.cancelled_shipments}</b></span>
        {Object.entries(data.payment_modes).map(([k, v]) => <span key={k}>{k}: <b>{v}</b></span>)}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-slate-500 mb-2">Billed per month (last 24 months)</div>
        <div className="flex items-end gap-1 h-28">
          {data.monthly.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col justify-end items-center group relative" title={`${m.month}: ${m.shipments} shipments, ${inr(m.billed)}`}>
              <div className="w-full bg-blue-500/80 rounded-t" style={{ height: `${Math.max(m.billed ? 4 : 1, (m.billed / maxBilled) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>{data.monthly[0]?.month}</span><span>{data.monthly[data.monthly.length - 1]?.month}</span></div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
          <div className="text-xs font-semibold text-slate-500 mb-2">Recent shipments</div>
          {data.recent_shipments.length === 0 ? <p className="text-sm text-slate-400">No shipments yet.</p> : (
            <table className="w-full text-xs"><thead><tr className="text-left text-slate-400"><th className="py-1">Date</th><th>Order</th><th>Route</th><th>Status</th><th className="text-right">Billed</th></tr></thead>
              <tbody>{data.recent_shipments.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="py-1">{day(s.date)}</td><td className="font-mono">{s.order_id || "—"}</td>
                  <td>{s.from || "?"} → {s.to || "?"}</td><td>{s.status}</td><td className="text-right font-semibold">{inr(s.amount)}</td></tr>))}</tbody></table>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
          <div className="text-xs font-semibold text-slate-500 mb-2">Recent invoices</div>
          {data.recent_invoices.length === 0 ? <p className="text-sm text-slate-400">No invoices yet.</p> : (
            <table className="w-full text-xs"><thead><tr className="text-left text-slate-400"><th className="py-1">Date</th><th>Invoice</th><th>Status</th><th className="text-right">Amount</th></tr></thead>
              <tbody>{data.recent_invoices.map((i) => (
                <tr key={i.id} className="border-t border-slate-100">
                  <td className="py-1">{day(i.date)}</td>
                  <td><Link className="text-blue-700 underline font-mono" to={`/admin/invoices/${i.id}`}>{i.invoice_number}</Link></td>
                  <td><span className={`px-2 py-0.5 rounded-full ${badge[String(i.payment_status).toUpperCase()] || "bg-slate-100 text-slate-600"}`}>{i.payment_status || "—"}</span></td>
                  <td className="text-right font-semibold">{inr(i.amount)}</td></tr>))}</tbody></table>
          )}
        </div>
      </div>
    </div>
  );
}
